import {
  Box,
  Button,
  CircularProgress,
  Grid2,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useState } from "react";
import { viewPDF } from "../../../utils/viewPDF";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { useLocation, useNavigate } from "react-router-dom";
import { uploadFile } from "../../../utils/uploadToAzure";

const Step1 = ({
  rfq,
  setStep,
  bid,
  setBid,
  storedUser,
  fileNames,
  setFileNames,
}) => {
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const vendorId = JSON.parse(localStorage.getItem("vendor"));
  const [btnLoading, setBtnLoading] = useState(false);
  const [uploading, setUploading] = useState({
    attachment1: false,
    attachment2: false,
    attachment3: false,
  });
  const location = useLocation();

  const handleChange = (e) => {
    setBid({ ...bid, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const handleFileUpload = async (e) => {
    const { name, files } = e.target;
    setUploading((prev) => ({
      ...prev,
      [name]: true,
    }));
    if (files && files.length > 0) {
      const file = files[0]; // Get the first file

      // Update the file names state
      setFileNames((prev) => ({
        ...prev,
        [name]: file.name, // Set the filename in state
      }));

      const formData = new FormData();
      formData.append("file", file);

      try {
        const res = await uploadFile(file, rfq.orgId, rfq.rfqId, storedUser)
        setBid({ ...bid, [e.target.name]: res });
        setErrors({ ...errors, [e.target.name]: "" });
      } catch (error) {
        console.error("File upload error: Please upload file upto 1MB");
        console.error("File upload error:", error);
      } finally {
        setUploading((prev) => ({
          ...prev,
          [name]: false,
        }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const data = bid || {};

    const isEmpty = (value) => !value || value.trim?.() === "";

    if (rfq?.term1) {
      if (isEmpty(data.deliveryTerms)) {
        newErrors.deliveryTerms = "Delivery period (Lead Time) are required";
      } else if (data.deliveryTerms.length > 1000) {
        newErrors.deliveryTerms =
          "Delivery period (Lead Time) should be upto 1000 characters";
      }
    }
    if (rfq?.term2) {
      if (isEmpty(data.paymentTerms)) {
        newErrors.paymentTerms = "Payment Terms are required";
      } else if (data.paymentTerms.length > 1000) {
        newErrors.paymentTerms =
          "Payment Terms should be up to 1000 characters";
      }
    }
    if (rfq?.term3) {
      if (isEmpty(data.freight)) {
        newErrors.freight = "Freight is required";
      } else if (data.freight.length > 1000) {
        newErrors.freight = "Freight should be up to 1000 characters";
      }
    }
    if (rfq?.term4) {
      if (isEmpty(data.priceBasis)) {
        newErrors.priceBasis = "Price Basis (Incoterms) is required";
      } else if (data.priceBasis.length > 1000) {
        newErrors.priceBasis =
          "Price Basis (Incoterms) should be up to 1000 characters";
      }
    }
    if (rfq?.term5) {
      if (isEmpty(data.warrantyPeriod)) {
        newErrors.warrantyPeriod = "Warranty Period is required";
      } else if (data.warrantyPeriod.length > 1000) {
        newErrors.warrantyPeriod =
          "Warranty Period should be up to 1000 characters";
      }
    }
    if (rfq?.term6) {
      if (isEmpty(data.deliveryClause)) {
        newErrors.deliveryClause = "Delivery Clause is required";
      } else if (data.deliveryClause.length > 1000) {
        newErrors.deliveryClause =
          "Delivery Clause should be up to 1000 characters";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    if (!validateForm()) return;
    setBtnLoading(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/draft-bid`,
        {
          vendorId: vendorId,
          rfqId: rfq.rfqId,
          deliveryTerms: bid?.deliveryTerms || "N/A",
          paymentTerms: bid?.paymentTerms || "N/A",
          freight: bid?.freight || "N/A",
          priceBasis: bid?.priceBasis || "N/A",
          warrantyPeriod: bid?.warrantyPeriod || "N/A",
          deliveryClause: bid?.deliveryClause || "N/A",
          attachment1: bid?.attachment1 || "N/A",
          attachment1Filename: fileNames?.attachment1 || "N/A",
          attachment2: bid?.attachment2 || "N/A",
          attachment2Filename: fileNames?.attachment2 || "N/A",
          attachment3: bid?.attachment3 || "N/A",
          attachment3Filename: fileNames?.attachment3 || "N/A",
        },
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } }
      );
      setStep((prev) => prev + 1);
    } catch (error) {
      console.log(error);
    } finally {
      setBtnLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!validateForm()) return;
    setBtnLoading(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/draft-bid`,
        {
          vendorId: vendorId,
          rfqId: rfq.rfqId,
          deliveryTerms: bid.deliveryTerms || "",
          paymentTerms: bid.paymentTerms || "",
          freight: bid.freight || "",
          priceBasis: bid.priceBasis || "",
          warrantyPeriod: bid.warrantyPeriod || "",
          deliveryClause: bid.deliveryClause || "",
          attachment1: bid?.attachment1 || "N/A",
          attachment1Filename: fileNames?.attachment1 || "N/A",
          attachment2: bid?.attachment2 || "N/A",
          attachment2Filename: fileNames?.attachment2 || "N/A",
          attachment3: bid?.attachment3 || "N/A",
          attachment3Filename: fileNames?.attachment3 || "N/A",
        },
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } }
      );
      setStep((prev) => prev + 1);
    } catch (error) {
      console.log(error);
    } finally {
      setBtnLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/vendor/dashboard");
  };

  const removeAttachment = (attachmentKey) => {
    setBid((prevBid) => ({
      ...prevBid,
      [attachmentKey]: "",
      [`${attachmentKey}Filename`]: "",
    }));

    setFileNames((prevFileNames) => ({
      ...prevFileNames,
      [attachmentKey]: "",
    }));
  };

  return (
    <div style={{ position: "relative" }}>
      {btnLoading && <div className="loading-div"></div>}
      <div className="dashboard-container">
        <Typography className="form-title">Commercial Terms</Typography>
        <Grid2 container columnSpacing={2}>
          {rfq && rfq.term1 && (
            <Grid2 size={{ xs: 12, sm: 12 }}>
              <label htmlFor="deliveryTerms">
                Delivery period (Lead Time)
                <span style={{ color: "#FF0000" }}>*</span>
              </label>
              <textarea
                className={`${errors.deliveryTerms && "input-error"}`}
                name="deliveryTerms"
                id="deliveryTerms"
                value={(bid && bid.deliveryTerms) || ""}
                onChange={handleChange}
              ></textarea>
              {errors.deliveryTerms && (
                <p className="error">{errors.deliveryTerms}</p>
              )}
            </Grid2>
          )}
          {rfq && rfq.term2 && (
            <Grid2 size={{ xs: 12, sm: 12 }}>
              <label htmlFor="paymentTerms">
                Payment Terms<span style={{ color: "#FF0000" }}>*</span>
              </label>
              <textarea
                className={`${errors.paymentTerms && "input-error"}`}
                name="paymentTerms"
                id="paymentTerms"
                value={bid && bid.paymentTerms}
                onChange={handleChange}
              ></textarea>
              {errors.paymentTerms && (
                <p className="error">{errors.paymentTerms}</p>
              )}
            </Grid2>
          )}
          {rfq && rfq.term3 && (
            <Grid2 size={{ xs: 12, sm: 12 }}>
              <label htmlFor="freight">
                Freight<span style={{ color: "#FF0000" }}>*</span>
              </label>
              <textarea
                className={`${errors.freight && "input-error"}`}
                name="freight"
                id="freight"
                value={bid && bid.freight}
                onChange={handleChange}
              ></textarea>
              {errors.freight && <p className="error">{errors.freight}</p>}
            </Grid2>
          )}
          {rfq && rfq.term4 && (
            <Grid2 size={{ xs: 12, sm: 12 }}>
              <label htmlFor="priceBasis">
                Price Basis (Incoterms)
                <span style={{ color: "#FF0000" }}>*</span>
              </label>
              <textarea
                className={`${errors.priceBasis && "input-error"}`}
                name="priceBasis"
                id="priceBasis"
                value={bid && bid.priceBasis}
                onChange={handleChange}
              ></textarea>
              {errors.priceBasis && (
                <p className="error">{errors.priceBasis}</p>
              )}
            </Grid2>
          )}
          {rfq && rfq.term5 && (
            <Grid2 size={{ xs: 12, sm: 12 }}>
              <label htmlFor="warrantyPeriod">
                Warranty Period<span style={{ color: "#FF0000" }}>*</span>
              </label>
              <textarea
                className={`${errors.warrantyPeriod && "input-error"}`}
                name="warrantyPeriod"
                id="warrantyPeriod"
                value={bid && bid.warrantyPeriod}
                onChange={handleChange}
              ></textarea>
              {errors.warrantyPeriod && (
                <p className="error">{errors.warrantyPeriod}</p>
              )}
            </Grid2>
          )}
          {rfq && rfq.term6 && (
            <Grid2 size={{ xs: 12, sm: 12 }}>
              <label htmlFor="deliveryClause">
                Late Delivery Clause<span style={{ color: "#FF0000" }}>*</span>
              </label>
              <textarea
                className={`${errors.deliveryClause && "input-error"}`}
                name="deliveryClause"
                id="deliveryClause"
                value={bid && bid.deliveryClause}
                onChange={handleChange}
              ></textarea>
              {errors.deliveryClause && (
                <p className="error">{errors.deliveryClause}</p>
              )}
            </Grid2>
          )}
          {rfq?.document && (
            <Grid2 size={{ xs: 12, sm: 12 }}>
              <div>
                <label htmlFor="">Attachment from Buyer</label>
              </div>
              <Button
                className="btn-outlined btn-view-doc"
                onClick={() => viewPDF(rfq?.document, storedUser)}
              >
                View Document
              </Button>
            </Grid2>
          )}
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="attachment1">
              Attachment 1
              <div
                className={`input-file ${errors.attachment1 && "input-error"}`}
              >
                <input
                  type="file"
                  id="attachment1"
                  name="attachment1"
                  onChange={handleFileUpload}
                />
                <span
                  style={{
                    width: "80%",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                  }}
                >
                  {fileNames.attachment1 || "No file chosen"}
                </span>
                <AttachFileIcon
                  fontSize="small"
                  sx={{
                    color: "#555",
                    transform: "rotate(90deg)",
                    "&:hover": { color: "#1A71F6BF" },
                  }}
                />
              </div>
            </label>
            {uploading.attachment1 && <span style={{fontSize: '12px'}}>Uploading File... <CircularProgress size="30px" /></span>}
            {bid && bid.attachment1 && bid.attachment1 !== "N/A" && (
              <>
                <Button
                  sx={{
                    margin: "-6% 0 0 0 !important",
                    width: "max-content !important",
                    fontSize: "12px !important",
                    textDecoration: "underline",
                    textAlign: "start",
                  }}
                  onClick={() => viewPDF(bid.attachment1, storedUser)}
                >
                  {fileNames.attachment1}
                </Button>
                <Button
                  variant="text"
                  color="error"
                  sx={{
                    border: "1px solid",
                    display: "block",
                    minWidth: 0,
                    paddingY: 0,
                    fontSize: "12px",
                  }}
                  onClick={() => removeAttachment("attachment1")}
                >
                  Remove
                </Button>
              </>
            )}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="attachment2">
              Attachment 2
              <div
                className={`input-file ${errors.attachment2 && "input-error"}`}
              >
                <input
                  type="file"
                  id="attachment2"
                  name="attachment2"
                  onChange={handleFileUpload}
                />
                <span
                  style={{
                    width: "80%",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                  }}
                >
                  {fileNames.attachment2 || "No file chosen"}
                </span>
                <AttachFileIcon
                  fontSize="small"
                  sx={{
                    color: "#555",
                    transform: "rotate(90deg)",
                    "&:hover": { color: "#1A71F6BF" },
                  }}
                />
              </div>
            </label>
            {uploading.attachment2 && <span style={{fontSize: '12px'}}>Uploading File... <CircularProgress size="30px" /></span>}
            {bid && bid.attachment2 && bid.attachment2 !== "N/A" && (
              <>
                <Button
                  sx={{
                    margin: "-6% 0 0 0 !important",
                    width: "max-content !important",
                    fontSize: "12px !important",
                    textDecoration: "underline",
                    textAlign: "start",
                  }}
                  onClick={() => viewPDF(bid.attachment2, storedUser)}
                >
                  {fileNames.attachment2}
                </Button>
                <Button
                  variant="text"
                  color="error"
                  sx={{
                    border: "1px solid",
                    display: "block",
                    minWidth: 0,
                    paddingY: 0,
                    fontSize: "12px",
                  }}
                  onClick={() => removeAttachment("attachment2")}
                >
                  Remove
                </Button>
              </>
            )}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="attachment3">
              Attachment 3
              <div
                className={`input-file ${errors.attachment3 && "input-error"}`}
              >
                <input
                  type="file"
                  id="attachment3"
                  name="attachment3"
                  onChange={handleFileUpload}
                />
                <span
                  style={{
                    width: "80%",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                  }}
                >
                  {fileNames.attachment3 || "No file chosen"}
                </span>
                <AttachFileIcon
                  fontSize="small"
                  sx={{
                    color: "#555",
                    transform: "rotate(90deg)",
                    "&:hover": { color: "#1A71F6BF" },
                  }}
                />
              </div>
            </label>
            {uploading.attachment3 && <span style={{fontSize: '12px'}}>Uploading File... <CircularProgress size="30px" /></span>}
            {bid && bid.attachment3 && bid.attachment3 !== "N/A" && (
              <>
                <Button
                  sx={{
                    margin: "-6% 0 0 0 !important",
                    width: "max-content !important",
                    fontSize: "12px !important",
                    textDecoration: "underline",
                    textAlign: "start",
                  }}
                  onClick={() => viewPDF(bid.attachment3, storedUser)}
                >
                  {fileNames.attachment3}
                </Button>
                <Button
                  variant="text"
                  color="error"
                  sx={{
                    border: "1px solid",
                    display: "block",
                    minWidth: 0,
                    paddingY: 0,
                    fontSize: "12px",
                  }}
                  onClick={() => removeAttachment("attachment3")}
                >
                  Remove
                </Button>
              </>
            )}
          </Grid2>
        </Grid2>
      </div>
      <Box className="btn-container" sx={{ gap: 3 }}>
        <Button className="btn-outlined" onClick={handleBack}>
          Back
        </Button>
        <Button
          className="btn-contained"
          onClick={
            (bid && bid.draftBidId) || location.pathname.includes("draft")
              ? handleUpdate
              : handleNext
          }
        >
          {(bid && bid.draftBidId) || location.pathname.includes("draft")
            ? "Update"
            : "Next"}
        </Button>
      </Box>
    </div>
  );
};

export default Step1;